import {
  isMapleClawAvailable,
  mapleClawGateway,
  type MapleClawConfig,
  type MapleClawProcessingRuntime,
  type MapleClawTranscriptEntry,
} from '@/lib/mapleclawGateway';

export type MapleClawCliOutput = {
  text: string;
  type: 'normal' | 'error' | 'success' | 'warning' | 'info' | 'path' | 'file' | 'folder';
};

export type MapleClawCliResult = {
  output: MapleClawCliOutput[];
  openWindow?: {
    appId: string;
    title: string;
  };
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function normalizeFlagKey(token: string) {
  return token.replace(/^-+/, '').trim().toLowerCase();
}

function parseFlags(tokens: string[]) {
  const flags: Record<string, string | boolean> = {};
  const positionals: string[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (!token.startsWith('-')) {
      positionals.push(token);
      continue;
    }

    const key = normalizeFlagKey(token);
    const next = tokens[index + 1];
    if (next && !next.startsWith('-')) {
      flags[key] = next;
      index += 1;
      continue;
    }

    flags[key] = true;
  }

  return { flags, positionals };
}

function parseBoolean(value: string | boolean | undefined) {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase();
  if (['1', 'true', 'on', 'yes', 'enabled'].includes(normalized)) return true;
  if (['0', 'false', 'off', 'no', 'disabled'].includes(normalized)) return false;
  return null;
}

function parseRuntime(value: unknown): MapleClawProcessingRuntime | null {
  return value === 'pi-agent' ? 'pi-agent' : value === 'local' ? 'local' : null;
}

function formatDate(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatRuntime(value: MapleClawProcessingRuntime) {
  return value === 'pi-agent' ? 'pi-agent' : 'local';
}

function formatTranscriptLine(entry: MapleClawTranscriptEntry): MapleClawCliOutput {
  const time = new Date(entry.timestamp).toLocaleTimeString();
  const roleLabel = entry.role === 'assistant' ? 'assistant' : entry.role === 'system' ? 'system' : 'user';
  const prefix = `[${time}] ${roleLabel}> `;

  if (entry.content.includes('\n')) {
    return {
      text: `${prefix}\n${entry.content}`,
      type: entry.role === 'assistant' ? 'success' : entry.role === 'system' ? 'info' : 'normal',
    };
  }

  return {
    text: `${prefix}${entry.content}`,
    type: entry.role === 'assistant' ? 'success' : entry.role === 'system' ? 'info' : 'normal',
  };
}

function buildHelpOutput(): MapleClawCliOutput[] {
  return [
    { text: 'MapleClaw terminal commands:', type: 'info' },
    { text: '  mapleclaw help', type: 'normal' },
    { text: '  mapleclaw open', type: 'normal' },
    { text: '  mapleclaw status', type: 'normal' },
    { text: '  mapleclaw sessions', type: 'normal' },
    { text: '  mapleclaw tail [session] [limit]', type: 'normal' },
    { text: '  mapleclaw ping [session]', type: 'normal' },
    { text: '  mapleclaw ask [--session <key>] [--runtime <local|pi-agent>] <message>', type: 'normal' },
    { text: '  mapleclaw config', type: 'normal' },
    { text: '  mapleclaw config set default-session <key>', type: 'normal' },
    { text: '  mapleclaw config set runtime <local|pi-agent>', type: 'normal' },
    { text: '  mapleclaw config set heartbeat <minutes>', type: 'normal' },
    { text: '  mapleclaw config set auto-heartbeat <on|off>', type: 'normal' },
    { text: '  mapleclaw config set workspace <path>', type: 'normal' },
    { text: '  mapleclaw config set pi-model <modelId>', type: 'normal' },
    { text: '  mapleclaw config set pi-instructions "..."', type: 'normal' },
    { text: '', type: 'normal' },
    { text: 'Examples:', type: 'info' },
    { text: '  mapleclaw ask "summarize the current MapleClaw state"', type: 'normal' },
    { text: '  mapleclaw ask --session ops --runtime pi-agent "plan the next operator steps"', type: 'normal' },
    { text: '  mapleclaw tail ops 12', type: 'normal' },
  ];
}

function buildConfigOutput(config: MapleClawConfig): MapleClawCliOutput[] {
  return [
    { text: 'MapleClaw config:', type: 'info' },
    { text: `  workspace: ${config.workspacePath}`, type: 'path' },
    { text: `  default-session: ${config.defaultSessionKey}`, type: 'normal' },
    { text: `  runtime: ${formatRuntime(config.processingRuntime)}`, type: 'normal' },
    { text: `  heartbeat-minutes: ${Math.round(config.heartbeatMs / 60000)}`, type: 'normal' },
    { text: `  auto-heartbeat: ${config.autoHeartbeat ? 'on' : 'off'}`, type: 'normal' },
    { text: `  default-model: ${config.defaultModelId || 'auto'}`, type: 'normal' },
    { text: `  pi-model: ${config.piAgentModelId || 'auto'}`, type: 'normal' },
    { text: `  pi-instructions: ${config.piAgentInstructions?.trim() ? config.piAgentInstructions : '(none)'}`, type: 'normal' },
  ];
}

export async function executeMapleClawCli(args: string[]): Promise<MapleClawCliResult> {
  if (!isMapleClawAvailable()) {
    return {
      output: [{ text: 'MapleClaw is unavailable in the current runtime.', type: 'error' }],
    };
  }

  const subcommand = (args[0] || 'help').toLowerCase();
  const { flags, positionals } = parseFlags(args.slice(1));
  const config = await mapleClawGateway.getConfig();

  switch (subcommand) {
    case 'help':
    case '--help':
    case '-h':
      return { output: buildHelpOutput() };

    case 'open':
    case 'app':
    case 'ui':
      return {
        openWindow: { appId: 'mapleclaw', title: 'MapleClaw' },
        output: [{ text: 'Opening MapleClaw…', type: 'info' }],
      };

    case 'status': {
      const status = await mapleClawGateway.getStatus();
      return {
        output: [
          { text: 'MapleClaw status:', type: 'info' },
          { text: `  runtime-host: ${status.runtime}`, type: 'normal' },
          { text: `  running: ${status.running ? 'yes' : 'no'}`, type: 'normal' },
          { text: `  workspace: ${status.workspacePath}`, type: 'path' },
          { text: `  default-session: ${config.defaultSessionKey}`, type: 'normal' },
          { text: `  message-runtime: ${formatRuntime(status.processingRuntime)}`, type: 'normal' },
          { text: `  sessions: ${status.sessionCount}`, type: 'normal' },
          { text: `  active-queues: ${status.activeSessions}`, type: 'normal' },
          { text: `  auto-heartbeat: ${status.autoHeartbeat ? 'on' : 'off'}`, type: 'normal' },
          { text: `  heartbeat-minutes: ${Math.round(status.heartbeatMs / 60000)}`, type: 'normal' },
        ],
      };
    }

    case 'sessions':
    case 'ls': {
      const sessions = await mapleClawGateway.listSessions();
      if (!sessions.length) {
        return {
          output: [{ text: 'No MapleClaw sessions yet. Use mapleclaw ask or mapleclaw ping to create one.', type: 'info' }],
        };
      }

      return {
        output: [
          { text: 'MapleClaw sessions:', type: 'info' },
          ...sessions.map((session) => ({
            text: `  ${session.key}${session.key === config.defaultSessionKey ? ' *' : ''}  queue=${session.queueSize}  processing=${session.processing ? 'yes' : 'no'}  last=${formatDate(session.lastActivityAt)}`,
            type: session.key === config.defaultSessionKey ? 'success' as const : 'normal' as const,
          })),
        ],
      };
    }

    case 'tail':
    case 'history':
    case 'transcript': {
      const sessionKey = String(flags.session || flags.s || positionals[0] || config.defaultSessionKey);
      const limit = clamp(Number(flags.limit || positionals[1] || 12), 1, 60);
      const transcript = await mapleClawGateway.getTranscript(sessionKey, limit);

      if (!transcript.length) {
        return {
          output: [{ text: `No transcript entries found for session "${sessionKey}".`, type: 'warning' }],
        };
      }

      return {
        output: [
          { text: `MapleClaw transcript — session "${sessionKey}" (last ${limit})`, type: 'info' },
          ...transcript.map(formatTranscriptLine),
        ],
      };
    }

    case 'ping':
    case 'heartbeat': {
      const sessionKey = String(flags.session || flags.s || positionals[0] || config.defaultSessionKey);
      const result = await mapleClawGateway.enqueue({
        sessionKey,
        type: 'heartbeat',
        payload: 'Manual MapleClaw heartbeat from Terminal',
        awaitResponse: true,
      });

      if (!result.ok) {
        return {
          output: [{ text: result.error || 'MapleClaw heartbeat failed.', type: 'error' }],
        };
      }

      return {
        output: [
          { text: `Heartbeat recorded for session "${sessionKey}".`, type: 'success' },
          { text: result.content || 'HEARTBEAT_OK', type: 'info' },
        ],
      };
    }

    case 'ask':
    case 'send':
    case 'chat': {
      const sessionKey = String(flags.session || flags.s || config.defaultSessionKey);
      const runtime = parseRuntime(flags.runtime || flags.r) || config.processingRuntime;
      const modelId = typeof flags.model === 'string' ? flags.model : undefined;
      const agentInstruction = typeof flags.instruction === 'string' ? flags.instruction : undefined;
      const message = positionals.join(' ').trim();

      if (!message) {
        return {
          output: [{ text: `Usage: mapleclaw ${subcommand} [--session <key>] [--runtime <local|pi-agent>] <message>`, type: 'warning' }],
        };
      }

      const result = await mapleClawGateway.enqueue({
        sessionKey,
        type: 'user_message',
        payload: message,
        awaitResponse: true,
        processingRuntime: runtime,
        modelId,
        agentInstruction,
      });

      if (!result.ok) {
        return {
          output: [{ text: result.error || 'MapleClaw request failed.', type: 'error' }],
        };
      }

      return {
        output: [
          { text: `MapleClaw replied — session="${sessionKey}" runtime=${runtime}`, type: 'success' },
          ...String(result.content || '').split('\n').map((line) => ({
            text: line,
            type: 'normal' as const,
          })),
        ],
      };
    }

    case 'config': {
      if ((positionals[0] || '').toLowerCase() !== 'set') {
        return { output: buildConfigOutput(config) };
      }

      const field = (positionals[1] || '').toLowerCase();
      const rawValue = positionals.slice(2).join(' ').trim();
      if (!field || !rawValue) {
        return {
          output: [{ text: 'Usage: mapleclaw config set <default-session|runtime|heartbeat|auto-heartbeat|workspace|pi-model|pi-instructions> <value>', type: 'warning' }],
        };
      }

      let nextConfig: Partial<MapleClawConfig> | null = null;

      switch (field) {
        case 'session':
        case 'default-session':
          nextConfig = { defaultSessionKey: rawValue };
          break;
        case 'runtime': {
          const runtime = parseRuntime(rawValue);
          if (!runtime) {
            return { output: [{ text: 'Runtime must be either local or pi-agent.', type: 'error' }] };
          }
          nextConfig = { processingRuntime: runtime };
          break;
        }
        case 'heartbeat': {
          const minutes = Number(rawValue);
          if (!Number.isFinite(minutes) || minutes < 1) {
            return { output: [{ text: 'Heartbeat must be a number of minutes greater than or equal to 1.', type: 'error' }] };
          }
          nextConfig = { heartbeatMs: minutes * 60 * 1000 };
          break;
        }
        case 'auto-heartbeat':
        case 'autoheartbeat': {
          const parsed = parseBoolean(rawValue);
          if (parsed === null) {
            return { output: [{ text: 'auto-heartbeat must be on/off, true/false, or yes/no.', type: 'error' }] };
          }
          nextConfig = { autoHeartbeat: parsed };
          break;
        }
        case 'workspace':
          nextConfig = { workspacePath: rawValue };
          break;
        case 'pi-model':
        case 'pimodel':
          nextConfig = { piAgentModelId: rawValue };
          break;
        case 'pi-instructions':
        case 'piinstruction':
        case 'pi-instruction':
          nextConfig = { piAgentInstructions: rawValue };
          break;
        default:
          return {
            output: [{ text: `Unknown MapleClaw config field: ${field}`, type: 'error' }],
          };
      }

      await mapleClawGateway.setConfig(nextConfig);
      const updatedConfig = await mapleClawGateway.getConfig();
      return {
        output: [
          { text: `MapleClaw config updated: ${field}`, type: 'success' },
          ...buildConfigOutput(updatedConfig),
        ],
      };
    }

    default:
      return {
        output: [
          { text: `Unknown MapleClaw subcommand: ${subcommand}`, type: 'error' },
          ...buildHelpOutput(),
        ],
      };
  }
}
